import { useCallback, useEffect, useState } from 'react';
import Button from '../../../../components/atoms/button';
import Card from '../../../../components/atoms/card';
import InputCalendar from '../../../../components/molecules/input-calendar';
import Select from '../../../../components/atoms/select';
import Input from '../../../../components/atoms/input';
import TextArea from '../../../../components/atoms/text-area';
import SVGIcon from '../../../../components/atoms/icons';
import MultiImageUpload from '../../../../components/organisms/image-upload/multi';
import MaintenanceTypeModal from './modal';
import { ModalProps } from '../../../../components/template/mvc-modal';
import MaintenanceService, {
	IMaintenance,
	IMaintenanceType,
} from '../../../../services/Maintenance.service';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteLoaderData } from 'react-router-dom';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import YardService, { IYard } from '../../../../services/Yard.service';
import MaterialService, {
	IMaterial,
} from '../../../../services/Material.service';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import ContainerService, {
	IContainer,
} from '../../../../services/Container.service';

const initialState: IMaintenance = {
	maintenanceType: {
		description: '',
	},
	yard: {},
	description: '',
	date: new Date(),
	hh: '',
	voltage: '',
	size: '',
	manufactory: '',
	container: '',
	block: '',
	batch: '',
	place: 'Container',
	materials: [],
};

const modalInitialState: IMaintenanceType = {
	description: '',
};

const places = ['Container', 'Quadra / Lote'];

const voltages = ['115', '127', '220', '230', '240', '254', '380', '440'];

const manufactories = [
	'CARRIER',
	'DAIKIN',
	'DENSO',
	'MITSUBISHI',
	'STAR COOL',
	'THERMOKING',
];

const sizes = ['20RF', '40RF', '40HR'];

const NewMaintenance = () => {
	const { t } = useTranslation();
	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;
	const location = useLocation();

	const [form, setForm] = useState<IMaintenance>(
		location.state || initialState
	);
	const [maintenanceTypeData, setMaintenanceTypeData] = useState<
		IMaintenanceType[]
	>([]);
	const [yardsData, setYardsData] = useState<IYard[]>([]);
	const [materialsData, setMaterialsData] = useState<IMaterial[]>([]);
	const [containersData, setContainersData] = useState<IContainer[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const [modalProps, setModalProps] = useState<ModalProps<IMaintenanceType>>({
		data: modalInitialState,
		title: t('maintenance.type.new'),
		mode: 'create',
		isModalVisible: false,
		onClose: () => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: modalInitialState,
			}));
			fetchMaintenanceTypes();
		},
	});

	const [error, setError] = useState<string>('');

	const [images, setImages] = useState<File[]>([]);
	const [versaoUpload, setVersaoUpload] = useState(0);

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const isDisabled = !form.yard || form.yard.id === undefined;

	const fetchMaintenanceTypes = useCallback(async () => {
		if (modalProps.isModalVisible) return;
		const data = await MaintenanceService.getAllMaintenanceType();
		if (!data) return;
		setMaintenanceTypeData(data);
	}, [user, modalProps.isModalVisible]);

	useEffect(() => {
		fetchMaintenanceTypes();
	}, [fetchMaintenanceTypes]);

	const fetchYards = useCallback(async () => {
		const response = await YardService.getAll({
			isActive: 1,
			idClient: Number(accountId),
		});
		if (!response) return;
		setYardsData(response.data);
	}, []);

	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	const fetchMaterials = useCallback(async () => {
		const response = await MaterialService.getAll({
			isActive: 1,
		});
		if (!response) return;
		setMaterialsData(response.data);
	}, []);

	useEffect(() => {
		fetchMaterials();
	}, [fetchMaterials]);

	useEffect(() => {
		const fetchContainers = async () => {
			if (!form.yard || !form.yard.id) return;
			const containers = await ContainerService.getAllByYard(
				form.yard.id
			);
			if (containers) setContainersData(containers);
			else setContainersData([]);
		};
		fetchContainers();
	}, [form.yard]);

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setForm((prev) => ({
			...prev,
			[e.target.name]: e.target.value,
		}));
	};

	const handleDateChange = (date: Date) => {
		handleInput({ target: { name: 'date', value: date } });
	};

	const handleYard = (e: EditableChangeEvent) => {
		const yardUpdated = yardsData.find(
			(item) => item.name === e.target.value
		);
		if (!yardUpdated) return;
		handleInput({ target: { name: 'yard', value: yardUpdated } });
		handleInput({ target: { name: 'container', value: '' } });
	};

	const handleContainer = (e: EditableChangeEvent) => {
		const containerUpdated = containersData.find(
			(item) => item.label === e.target.value
		);
		if (!containerUpdated) return;
		handleInput({
			target: { name: 'container', value: containerUpdated.label },
		});
	};

	const handleMaintenanceType = (e: EditableChangeEvent) => {
		const maintenanceTypeUpdated = maintenanceTypeData.find(
			(item) => item.description === e.target.value
		);
		if (!maintenanceTypeUpdated) return;
		handleInput({
			target: { name: 'maintenanceType', value: maintenanceTypeUpdated },
		});
	};

	const handleMaterials = (e: EditableChangeEvent, index: number) => {
		const materials = [...form.materials];
		materials[index][
			e.target.name as keyof (IMaterial & { quantity: string })
		] = e.target.value;
		setForm((prev) => ({
			...prev,
			materials,
		}));
	};

	const onModalOpen = () => {
		setModalProps((prev) => ({
			...prev,
			isModalVisible: true,
		}));
	};

	const addMaterial = () => {
		const materials = [...form.materials];
		materials.push({
			name: '',
			quantity: '',
		});
		setForm((prev) => ({
			...prev,
			materials,
		}));
	};

	const removeMaterial = (index: number) => {
		const materials = [...form.materials];
		materials.splice(index, 1);
		setForm((prev) => ({
			...prev,
			materials,
		}));
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (
			!form.yard ||
			!form.yard.id ||
			!form.maintenanceType ||
			!form.maintenanceType.id ||
			!form.description ||
			!form.date
		) {
			setError('alert.error.mandatory');
			return;
		}
		setLoading(true);
		const { materials, ...data } = form;
		const materialsObj: (Partial<IMaintenance> & { quantity: string })[] =
			[];
		materials.map((material) => {
			const data = materialsData.find(
				(item) => item.name === material.name
			);
			if (data)
				materialsObj.push({ ...data, quantity: material.quantity });
		});
		const botao = (e.nativeEvent as SubmitEvent)
			.submitter as HTMLButtonElement | null;
		const response = await MaintenanceService.create(
			{
				...data,
				materials: materialsObj,
				imgFiles: images,
				enviarEmail: botao?.value !== 'false',
			},
			setError
		);
		if (response) {
			NotificationToast.Success(
				`${t('maintenance.title')} ${t('alert.success.create')}`
			);
			setForm(initialState);
			setImages([]);
			setVersaoUpload((anterior) => anterior + 1);
		}
		setLoading(false);
	};

	const handleLoadLocal = () => {
		const plainText = localStorage.getItem('manutencao');
		const { data } = JSON.parse(plainText || '');
		if (data) setForm(data);
	};

	return (
		<>
			<form
				className="grid grid-rows-[auto_auto] lg:grid-cols-2 gap-6"
				onSubmit={handleSubmit}
			>
				<Card className="grid grid-cols-2 p-6 gap-x-6 gap-y-4 h-fit">
					<InputCalendar
						date={form.date}
						onDateChange={handleDateChange}
						label={t('date.retroactive')}
						placeholder={t('date.retroactive')}
						required
					/>
					<Select
						name="yard"
						value={form.yard.name || ''}
						options={yardsData.map((item) => item.name)}
						label={t('port_management.yard')}
						placeholder={t('port_management.yard')}
						onChange={handleYard}
						required
					/>
					<Select
						name="voltage"
						value={form.voltage}
						onChange={handleInput}
						options={voltages}
						label={t('maintenance.voltage')}
						placeholder={t('maintenance.voltage')}
					/>
					<Input
						name="hh"
						value={form.hh}
						type="number"
						onChange={handleInput}
						label={t('maintenance.hh')}
						placeholder={t('maintenance.hh')}
					/>
					<Select
						name="place"
						value={form.place}
						onChange={handleInput}
						options={places}
						label={t('misc.place')}
						placeholder={t('misc.place')}
					/>
					{form.place === 'Container' ? (
						<>
							<Select
								name="size"
								value={form.size}
								onChange={handleInput}
								options={sizes}
								label={t('misc.size')}
								placeholder={t('misc.size')}
							/>
							<Select
								name="manufactory"
								value={form.manufactory}
								onChange={handleInput}
								options={manufactories}
								label={t('maintenance.manufactory')}
								placeholder={t('maintenance.manufactory')}
							/>
							<Select
								name="container"
								value={form.container}
								onChange={handleContainer}
								options={containersData.map(
									(container) => container.label
								)}
								label={t('port_management.container')}
								placeholder={t('port_management.container')}
							/>
						</>
					) : (
						<>
							<Input
								name="block"
								value={form.block}
								onChange={handleInput}
								label={t('maintenance.block')}
								placeholder={t('maintenance.block')}
							/>
							<Input
								name="batch"
								value={form.batch}
								onChange={handleInput}
								label={t('maintenance.batch')}
								placeholder={t('maintenance.batch')}
							/>
						</>
					)}
					<TextArea
						containerClassName="col-span-2"
						name="description"
						value={form.description}
						onChange={handleInput}
						rows={5}
						label={t('misc.description')}
						placeholder={t('misc.description')}
						required
					/>
				</Card>
				<Card className="flex flex-col p-6 gap-x-6 gap-y-3 h-fit">
					<div className="grid grid-cols-[1fr_auto] gap-x-6 items-center">
						<Select
							name="maintenanceType"
							value={form.maintenanceType?.description || ''}
							onChange={handleMaintenanceType}
							options={maintenanceTypeData.map(
								(item) => item.description
							)}
							label={t('maintenance.type.title')}
							placeholder={t('maintenance.type.title')}
							required
						/>
						<Button onClick={onModalOpen}>
							{t('maintenance.type.new')}
						</Button>
					</div>
					{form.materials.map((item, index) => (
						<div
							className="grid grid-cols-[2fr_1fr_auto] gap-x-6 items-center"
							key={index}
						>
							<Select
								name="name"
								value={form.materials[index].name}
								onChange={(e) => handleMaterials(e, index)}
								options={materialsData.map((item) => item.name)}
								label={t('port_management.material')}
								placeholder={t('port_management.material')}
							/>
							<Input
								name="quantity"
								value={form.materials[index].quantity || ''}
								onChange={(e) => handleMaterials(e, index)}
								label={t('misc.quantity')}
								placeholder={t('misc.quantity')}
								type="number"
							/>
							<SVGIcon
								className="fill-red h-4 w-auto cursor-pointer"
								icon="Delete"
								onClick={() => removeMaterial(index)}
							/>
						</div>
					))}
					<Button className="w-fit" onClick={addMaterial}>
						{t('port_management.materials.add')}
					</Button>
					<MultiImageUpload
						key={versaoUpload}
						files={images}
						setFiles={setImages}
					/>
				</Card>
				<div className="lg:col-span-2 flex gap-3 items-center justify-end h-fit">
					<Button type="button" onClick={handleLoadLocal}>
						{t('misc.previous_maintenance')}
					</Button>
					<Button
						icon="Save"
						type="submit"
						value="false"
						disabled={loading}
					>
						{t('misc.save')}
					</Button>
					<Button
						className="w-fit"
						icon="Share"
						type="submit"
						disabled={loading}
					>
						{t('misc.save_send')}
					</Button>
				</div>
			</form>
			<MaintenanceTypeModal
				setModalProps={setModalProps}
				{...modalProps}
			/>
		</>
	);
};

export default NewMaintenance;
